import { useModalDismiss } from '../hooks/useModalDismiss';
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArchive, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../Api';

const DEFAULT_CONSEQUENCES = [
  'Le compte sera désactivé : la personne ne pourra plus se connecter.',
  'Son historique est conservé et reste consultable.',
  'Vous pourrez la restaurer à tout moment depuis la page des archives.',
];

const ArchiveUserModal = ({
  isOpen,
  onClose,
  user,
  onArchive,
  archiveEndpoint,
  archiveMethod = 'POST',
  consequences = DEFAULT_CONSEQUENCES,
  // Le composant sert aussi aux contrôleurs et aux enquêteurs : ces libellés étaient
  // codés en dur sur « responsable ».
  title = 'Archiver le Responsable',
  subtitle = 'Cette action enverra le responsable vers les archives',
  entityLabel = 'responsable',
}) => {
  const [archiveReason, setArchiveReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!archiveReason.trim()) {
      setError('Veuillez fournir une raison pour l\'archivage');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use custom endpoint if provided, otherwise use default
      const endpoint = archiveEndpoint || `/users/${user.id}/archive`;
      
      let response;
      if (archiveMethod === 'DELETE') {
        response = await api.delete(endpoint, {
          data: { archive_reason: archiveReason.trim() }
        });
      } else {
        response = await api.post(endpoint, {
          archive_reason: archiveReason.trim()
        });
      }

      // La réponse porte le rapport de redistribution du travail : la remonter à l'appelant.
      onArchive(response?.data);
      onClose();
      setArchiveReason('');
    } catch (error) {
      console.error('Error archiving user:', error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError(`Erreur lors de l'archivage du ${entityLabel}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setArchiveReason('');
      setError('');
      onClose();
    }
  };

  // Escape only: this overlay has never closed on a backdrop click.
  useModalDismiss(handleClose, { backdropClose: false, enabled: isOpen });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
              <FontAwesomeIcon icon={faArchive} className="text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {title}
              </h3>
              <p className="text-sm text-gray-500">
                {subtitle}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <FontAwesomeIcon icon={faArchive} className="text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">{user.name}</h4>
                <p className="text-sm text-gray-600">{user.email}</p>
                <p className="text-xs text-gray-500">{user.role_display || user.ref}</p>
              </div>
            </div>
          </div>

          {/* Ce que fait réellement l'archivage */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-blue-900 mb-2">Ce qui va se passer</p>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              {consequences.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Raison de l'archivage *
              </label>
              <textarea
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder={`Expliquez pourquoi ce ${entityLabel} est archivé...`}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none"
                rows={4}
                required
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Cette raison sera visible dans les archives
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading || !archiveReason.trim()}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Archivage...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faArchive} className="mr-2" />
                    Archiver
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ArchiveUserModal;
